/**
 * IndexedDB Database Wrapper
 * 
 * Provides a robust wrapper around IndexedDB with transaction management,
 * error handling, and schema migration support.
 */

import { DB_NAME, DB_VERSION, STORES, INDEXES, SCHEMA_VERSIONS } from './schema';

export class OfflineDatabase {
  private db: IDBDatabase | null = null;
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize the database connection
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._initialize();
    return this.initPromise;
  }

  private async _initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) {
        reject(new Error('IndexedDB is not supported in this browser'));
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error(`Failed to open database: ${request.error?.message}`));
      };

      request.onsuccess = () => {
        this.db = request.result;

        // Verify all required object stores exist
        const missingStores = Object.values(STORES).filter(storeName =>
          !this.db!.objectStoreNames.contains(storeName)
        );

        if (missingStores.length > 0) {
          console.error('❌ Missing object stores:', missingStores);

          // Close the database and delete it to force recreation
          this.db!.close();
          this.db = null;
          this.isInitialized = false;

          const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
          deleteRequest.onsuccess = () => {
            // Retry initialization
            this._initialize().then(resolve).catch(reject);
          };
          deleteRequest.onerror = () => {
            reject(new Error('Failed to delete corrupted database'));
          };
          return;
        }

        this.isInitialized = true;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = (event.target as IDBOpenDBRequest).transaction!;

        this.createStores(db, transaction);
      };
    });
  }

  /**
   * Create object stores and indexes
   */
  private createStores(db: IDBDatabase, transaction: IDBTransaction): void {


    // Create all object stores
    Object.values(STORES).forEach(storeName => {
      try {
        if (!db.objectStoreNames.contains(storeName)) {
          const store = db.createObjectStore(storeName, { keyPath: 'id' });

          // Create indexes for this store
          const storeIndexes = INDEXES[storeName as keyof typeof INDEXES];
          if (storeIndexes) {
            storeIndexes.forEach(index => {
              try {
                if (!store.indexNames.contains(index.name)) {
                  store.createIndex(index.name, index.keyPath, { unique: false });
                }
              } catch (indexError) {
                console.error(`❌ Failed to create index ${index.name} for store ${storeName}:`, indexError);
              }
            });
          }
        }
      } catch (storeError) {
        console.error(`❌ Failed to create store ${storeName}:`, storeError);
        throw storeError;
      }
    });

    // Run any version-specific migrations
    const currentVersion = SCHEMA_VERSIONS.find(v => v.version === DB_VERSION);
    if (currentVersion?.migrations) {
      currentVersion.migrations.forEach(migration => {
        try {
          migration(db, transaction);
        } catch (migrationError) {
          console.error('❌ Migration failed:', migrationError);
          throw migrationError;
        }
      });
    }
  }

  /**
   * Execute a transaction with proper error handling
   */
  async transaction<T>(
    storeNames: string | string[],
    mode: IDBTransactionMode,
    operation: (transaction: IDBTransaction, stores: { [key: string]: IDBObjectStore }) => Promise<T>
  ): Promise<T> {
    await this.initialize();
    
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeNames, mode);
      
      // Create stores object for easy access
      const stores: { [key: string]: IDBObjectStore } = {};
      const storeArray = Array.isArray(storeNames) ? storeNames : [storeNames];
      storeArray.forEach(storeName => {
        stores[storeName] = tx.objectStore(storeName);
      });

      tx.oncomplete = () => {
        // Transaction completed successfully
      };

      tx.onerror = () => {
        reject(new Error(`Transaction failed: ${tx.error?.message}`));
      };

      tx.onabort = () => {
        reject(new Error('Transaction was aborted'));
      };

      // Execute the operation
      operation(tx, stores)
        .then(resolve)
        .catch(reject);
    });
  }

  /**
   * Get a single record by ID
   */
  async get<T>(storeName: string, id: string): Promise<T | null> {
    return this.transaction(storeName, 'readonly', async (_, stores) => {
      return new Promise<T | null>((resolve, reject) => {
        const request = stores[storeName].get(id);

        request.onsuccess = () => {
          resolve(request.result || null);
        };

        request.onerror = () => {
          reject(new Error(`Failed to get record: ${request.error?.message}`));
        };
      });
    });
  }

  /**
   * Get all records from a store
   */
  async getAll<T>(storeName: string): Promise<T[]> {
    return this.transaction(storeName, 'readonly', async (_, stores) => {
      return new Promise<T[]>((resolve, reject) => {
        const request = stores[storeName].getAll();

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = () => {
          reject(new Error(`Failed to get all records: ${request.error?.message}`));
        };
      });
    });
  }

  /**
   * Put (insert or update) a record
   */
  async put<T>(storeName: string, record: T): Promise<void> {
    return this.transaction(storeName, 'readwrite', async (_, stores) => {
      return new Promise<void>((resolve, reject) => {
        const request = stores[storeName].put(record);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(new Error(`Failed to put record: ${request.error?.message}`));
        };
      });
    });
  }

  /**
   * Put multiple records in a single transaction
   */
  async putMany<T>(storeName: string, records: T[]): Promise<void> {
    return this.transaction(storeName, 'readwrite', async (_, stores) => {
      const promises = records.map(record => {
        return new Promise<void>((resolve, reject) => {
          const request = stores[storeName].put(record);

          request.onsuccess = () => resolve();
          request.onerror = () => reject(new Error(`Failed to put record: ${request.error?.message}`));
        });
      });

      await Promise.all(promises);
    });
  }

  /**
   * Delete a record by ID
   */
  async delete(storeName: string, id: string): Promise<void> {
    return this.transaction(storeName, 'readwrite', async (_, stores) => {
      return new Promise<void>((resolve, reject) => {
        const request = stores[storeName].delete(id);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(new Error(`Failed to delete record: ${request.error?.message}`));
        };
      });
    });
  }

  /**
   * Query records using an index
   */
  async queryByIndex<T>(
    storeName: string,
    indexName: string,
    query: IDBValidKey | IDBKeyRange,
    limit?: number
  ): Promise<T[]> {
    return this.transaction(storeName, 'readonly', async (_, stores) => {
      return new Promise<T[]>((resolve, reject) => {
        const index = stores[storeName].index(indexName);
        const request = limit ? index.getAll(query, limit) : index.getAll(query);

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = () => {
          reject(new Error(`Failed to query by index: ${request.error?.message}`));
        };
      });
    });
  }

  /**
   * Count records in a store
   */
  async count(storeName: string, query?: IDBValidKey | IDBKeyRange): Promise<number> {
    return this.transaction(storeName, 'readonly', async (_, stores) => {
      return new Promise<number>((resolve, reject) => {
        const request = query ? stores[storeName].count(query) : stores[storeName].count();

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          reject(new Error(`Failed to count records: ${request.error?.message}`));
        };
      });
    });
  }

  /**
   * Clear all records from a store
   */
  async clear(storeName: string): Promise<void> {
    return this.transaction(storeName, 'readwrite', async (_, stores) => {
      return new Promise<void>((resolve, reject) => {
        const request = stores[storeName].clear();

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(new Error(`Failed to clear store: ${request.error?.message}`));
        };
      });
    });
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.isInitialized = false;
      this.initPromise = null;
    }
  }

  /**
   * Check if the database is initialized
   */
  get initialized(): boolean {
    return this.isInitialized;
  }
}

// Singleton instance
export const offlineDB = new OfflineDatabase();
